const dsStoreStockQueries = new Map();
const dsStoreStockInventory = new Map();
let dsStoreStockInventoryFlight = Promise.resolve();

function dsStoreStockAvailable(ids) {
  const load = async () => {
    const endpoint = document.querySelector('script[data-availability-endpoint]')?.dataset.availabilityEndpoint;
    if (!endpoint) throw new Error('Missing store availability service');
    const missing = ids.filter((id) => !(dsStoreStockInventory.get(String(id))?.expires > Date.now()));
    const batches = [];
    for (let i = 0; i < missing.length; i += 100) batches.push(missing.slice(i, i + 100));
    let next = 0;
    await Promise.all(Array.from({length: Math.min(2, batches.length)}, async () => {
      while (next < batches.length) {
        let pending = batches[next++];
        for (let attempt = 0; pending.length && attempt < 6; attempt++) {
          const url = new URL(endpoint, window.location.origin);
          url.searchParams.set('variants', JSON.stringify(pending.map((variantId) => ({variantId}))));
          const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
          if (!response.ok) {
            if ([429, 502, 503, 504].includes(response.status) && attempt < 5) {
              await new Promise((resolve) => setTimeout(resolve, Math.min(4000, 500 * 2 ** attempt)));
              continue;
            }
            throw new Error(`Store availability request failed (${response.status})`);
          }
          const result = await response.json();
          const items = new Map((result.items || []).map((item) => [String(item.variantId), item]));
          pending = pending.filter((id) => {
            const item = items.get(String(id));
            if (!item?.ok) return true;
            dsStoreStockInventory.set(String(id), {available: Boolean(item.available), expires: Date.now() + 60000});
            return false;
          });
        }
        if (pending.length) throw new Error('Incomplete store availability');
      }
    }));
    const available = new Set(ids.filter((id) => dsStoreStockInventory.get(String(id))?.available).map(String));
    while (dsStoreStockInventory.size > 10000) dsStoreStockInventory.delete(dsStoreStockInventory.keys().next().value);
    return available;
  };
  const result = dsStoreStockInventoryFlight.then(load);
  dsStoreStockInventoryFlight = result.catch(() => {});
  return result;
}

async function dsStoreStockProducts(searchParams) {
  const params = new URLSearchParams(searchParams);
  ['page', 'section_id', 'store_stock', 'filter.v.availability'].forEach((name) => params.delete(name));
  params.sort();
  const key = params.toString();
  const cached = dsStoreStockQueries.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = (async () => {
    const readIndex = async (page) => {
      const query = new URLSearchParams(params);
      query.set('section_id', 'ds-stock-filter-index');
      query.set('page', page);
      const response = await fetch(`${window.location.pathname}?${query}`);
      if (!response.ok) throw new Error('Collection index request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = doc.querySelector('[data-stock-filter-index]');
      if (!node) throw new Error('Missing stock index');
      return JSON.parse(node.textContent);
    };
    const first = await readIndex(1);
    const products = [...first.products];
    for (let page = 2; page <= first.pages; page++) products.push(...(await readIndex(page)).products);
    const ids = [...new Set(products.flatMap((product) => product.storeVariants).map(String))];
    const available = await dsStoreStockAvailable(ids);
    return products.map((product) => ({...product, storeVariants: product.storeVariants.filter((id) => available.has(String(id)))}))
      .filter((product) => product.storeVariants.length);
  })();
  dsStoreStockQueries.set(key, {promise, expires: Date.now() + 60000});
  if (dsStoreStockQueries.size > 20) dsStoreStockQueries.delete(dsStoreStockQueries.keys().next().value);
  try { return await promise; }
  catch (error) { dsStoreStockQueries.delete(key); throw error; }
}

function dsStoreStockFacetGroups(doc) {
  const groups = new Map();
  doc.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
    if (input.name === 'filter.v.availability') return;
    if (!groups.has(input.name)) groups.set(input.name, {name: input.name, and: input.classList.contains('filter-and'), inputs: [], values: new Set()});
    const group = groups.get(input.name);
    group.inputs.push(input);
    group.values.add(input.value);
  });
  return [...groups.values()];
}

async function dsStoreStockFacetCounts(doc, searchParams, currentProducts) {
  const params = new URLSearchParams(searchParams);
  for (const group of dsStoreStockFacetGroups(doc)) {
    const query = new URLSearchParams(params);
    const excludeOwn = !group.and && params.getAll(group.name).length > 0;
    if (excludeOwn) query.delete(group.name);
    const products = excludeOwn ? await dsStoreStockProducts(query) : currentProducts;
    const counts = new Map([...group.values].map((value) => [value, 0]));
    let supported = true;
    for (const product of products) {
      const sets = group.name.startsWith('filter.v.')
        ? product.storeVariants.map((id) => product.variantFacets?.[String(id)]?.[group.name])
        : [product.productFacets?.[group.name]];
      if (sets.some((values) => !Array.isArray(values))) {supported = false; break;}
      for (const value of new Set(sets.flat().map(String))) {
        if (counts.has(value)) counts.set(value, counts.get(value) + 1);
      }
    }
    if (!supported) {
      for (const value of group.values) {
        const candidate = new URLSearchParams(query);
        if (group.and) {
          if (!candidate.getAll(group.name).includes(value)) candidate.append(group.name, value);
        } else {
          candidate.delete(group.name);
          candidate.append(group.name, value);
        }
        counts.set(value, (await dsStoreStockProducts(candidate)).length);
      }
    }
    for (const input of group.inputs) {
      const count = counts.get(input.value);
      const selected = params.getAll(group.name).includes(input.value);
      input.disabled = count === 0 && !selected;
      const label = input.closest('label');
      const badge = label?.querySelector('.ds-facet-value-count');
      if (badge) badge.textContent = count;
      label?.classList.toggle('cursor-pointer', count > 0 || selected);
    }
  }
  dsUpdateEmptyFacetGroups(doc);
}

function dsUpdateEmptyFacetGroups(root) {
  root.querySelectorAll('.js-filter[data-facet-param]').forEach((facet) => {
    const name = facet.dataset.facetParam;
    if (name === 'filter.v.availability' || name.startsWith('filter.v.price.')) return;
    const button = facet.querySelector('.facets__summary');
    if (!button) return;
    const inputs = [...facet.querySelectorAll('.ds-facet-values input[type="checkbox"]')];
    const empty = !inputs.some((input) => input.checked || !input.disabled);
    facet.dataset.facetEmpty = String(empty);
    button.disabled = empty;
    const label = button.dataset.facetLabel;
    if (label) button.setAttribute('aria-label', empty ? `${button.dataset.facetTitle} – ${button.dataset.facetEmptyLabel}` : label);
    if (empty) {
      facet.setAttribute('x-data', '{open: false}');
      button.setAttribute('aria-expanded', 'false');
    }
  });
}

function dsStoreStockFacetLoading(root, value) {
  for (const group of dsStoreStockFacetGroups(root)) {
    for (const input of group.inputs) {
      const badge = input.closest('label')?.querySelector('.ds-facet-value-count');
      if (badge) badge.textContent = value;
    }
  }
}

// Shopify combines availability with variant filters at product level. Rebuild the
// filtered pages from an index of variants that satisfy both on the same variant.
async function dsStockFilterPage(html, searchParams, sectionId) {
  const parse = (source) => new DOMParser().parseFromString(source, 'text/html');
  const doc = parse(html);
  const configNode = doc.querySelector('[data-stock-filter-config]');
  if (!configNode) return html;
  const config = JSON.parse(configNode.textContent);
  const params = new URLSearchParams(searchParams);
  const storeStock = params.get('store_stock') === '1';
  if (config.active !== 'true' && !storeStock) return html;
  const readSection = async (section, page) => {
    const query = new URLSearchParams(params);
    query.set('section_id', section);
    query.set('page', page);
    const response = await fetch(`${window.location.pathname}?${query}`);
    if (!response.ok) throw new Error('Collection request failed');
    return parse(await response.text());
  };
  const readIndex = async (page) => {
    const result = await readSection('ds-stock-filter-index', page);
    const node = result.querySelector('[data-stock-filter-index]');
    if (!node) throw new Error('Missing stock index');
    return JSON.parse(node.textContent);
  };
  let eligible;
  if (storeStock) {
    eligible = await dsStoreStockProducts(params);
  } else {
    const first = await readIndex(1);
    const products = [...first.products];
    for (let page = 2; page <= first.pages; page++) products.push(...(await readIndex(page)).products);
    eligible = products.filter((product) => product.variant.trim());
  }
  const pages = Math.max(1, Math.ceil(eligible.length / config.pageSize));
  const page = Math.min(pages, Math.max(1, Number(params.get('page')) || 1));
  const wanted = eligible.slice((page - 1) * config.pageSize, page * config.pageSize);
  const nativePages = [...new Set(wanted.map((product) => Math.floor(product.position / config.pageSize) + 1))];
  const documents = await Promise.all(nativePages.map((nativePage) =>
    nativePage === config.page ? doc : readSection(sectionId, nativePage)
  ));
  const cards = new Map();
  for (const result of documents) {
    result.querySelectorAll('[data-stock-product-id]').forEach((card) => cards.set(card.dataset.stockProductId, card));
  }
  let grid = doc.getElementById('items-grid');
  if (wanted.length) {
    if (!grid) {
      grid = documents.find((result) => result.getElementById('items-grid')).getElementById('items-grid').cloneNode(false);
      doc.getElementById('product-grid').replaceWith(grid);
    }
    grid.replaceChildren(...wanted.map((product) => {
      const card = cards.get(String(product.id));
      if (!card) throw new Error('Missing matching product card');
      return card.cloneNode(true);
    }));
  } else {
    const empty = doc.querySelector('[data-stock-empty]').content.cloneNode(true);
    const target = grid || doc.getElementById('product-grid');
    target.replaceChildren(empty);
    target.className = '';
  }
  const count = config.countTemplate.replace('__COUNT__', eligible.length);
  ['ProductCount', 'ProductCount_header', 'ProductCountDrawer'].forEach((id) => {
    const node = doc.getElementById(id);
    if (node) node.textContent = count;
  });
  if (storeStock) {
    await dsStoreStockFacetCounts(doc, params, eligible);
  } else {
    doc.querySelectorAll('input[type="checkbox"][checked]').forEach((input) => {
      if (!input.name.startsWith('filter.') || params.getAll(input.name).length !== 1) return;
      const badge = input.closest('label')?.querySelector('.ds-facet-value-count');
      if (badge) badge.textContent = eligible.length;
    });
  }
  doc.querySelectorAll('[data-store-stock-count]').forEach((node) => {node.textContent = eligible.length;});
  const actionCount = doc.getElementById('ProductCountDrawerAction');
  if (actionCount) actionCount.textContent = eligible.length;
  let pager = doc.querySelector('[data-stock-pagination]');
  if (!pager && wanted.length) {
    pager = doc.createElement('div');
    pager.setAttribute('data-stock-pagination', '');
    grid.after(pager);
  }
  if (pager) {
    pager.replaceChildren();
    if (pages > 1) {
      const nav = doc.createElement('nav');
      nav.className = 'flex items-center justify-center flex-wrap gap-2 mt-7 mb-7';
      nav.setAttribute('aria-label', 'Pagination');
      const addLink = (targetPage, label) => {
        const link = doc.createElement('a');
        const query = new URLSearchParams(params);
        query.delete('section_id');
        query.set('page', targetPage);
        link.href = `${window.location.pathname}?${query}`;
        link.textContent = label;
        link.className = 'px-3 py-2 border-b-2 border-solid';
        if (targetPage === page) link.setAttribute('aria-current', 'page');
        nav.append(link);
      };
      if (page > 1) addLink(page - 1, config.previous);
      for (let n = 1; n <= pages; n++) {
        if (n === 1 || n === pages || Math.abs(n - page) <= 2) addLink(n, String(n));
        else if (n === page - 3 || n === page + 3) nav.append(doc.createTextNode('…'));
      }
      if (page < pages) addLink(page + 1, config.next);
      pager.append(nav);
    }
  }
  return doc.body.innerHTML;
}

document.addEventListener('alpine:init', () => {
  Alpine.data('xFiltersAndSortby', (sectionId) => ({
    t: '',
    show: false,
    showFilterAside: true,
    loading: false,
    isDesktop: false,
    listLayout: false,
    cachedResults: [],
    renderRequest: 0,
    filterUiState: null,
    storeStockCount: '…',
    stockCountRequest: 0,
    storeStockSelected: new URLSearchParams(window.location.search).get('store_stock') === '1',
    searchParamsPrev: window.location.search.slice(1),
    searchParamsInitial: window.location.search.slice(1),
    init() {
      if (localStorage.getItem("list-layout") == 'list') {
        this.listLayout = true;
      }
      this._initLayout();
      this._setListeners();
      const config = this.$root.querySelector('[data-stock-filter-config]');
      if (config && (JSON.parse(config.textContent).active === 'true' || this.storeStockSelected)) {
        this._renderPage(this.searchParamsInitial, false);
      } else if (config) {
        this._updateStoreStockCount(this.searchParamsInitial);
      }
    },
    async _updateStoreStockCount(searchParams) {
      const request = ++this.stockCountRequest;
      this.storeStockCount = '…';
      try {
        const products = await dsStoreStockProducts(searchParams);
        if (request === this.stockCountRequest) this.storeStockCount = products.length;
      } catch (error) {
        if (request === this.stockCountRequest) this.storeStockCount = '—';
      }
    },
    _initLayout() {
      let formAside = document.getElementsByClassName('form-aside')[0];
      let formDrawer = document.getElementsByClassName('form-drawer')[0];
      if (window.innerWidth > 767) {
        this.isDesktop = true;
        if (formAside) {
          formAside.setAttribute('id','FacetFiltersForm');
          if (formDrawer) formDrawer.removeAttribute('id');
        } else {
          if (formDrawer) formDrawer.setAttribute('id','FacetFiltersForm');
        }
      } else {
        this.isDesktop = false;
        if (formAside) formAside.removeAttribute('id');
        if (formDrawer) formDrawer.setAttribute('id','FacetFiltersForm');
      }
    },
    _setListeners() {
      const onHistoryChange = (event) => {
        const searchParams = event.state ? event.state.searchParams : this.searchParamsInitial;
        if (searchParams === this.searchParamsPrev) return;
        this._renderPage(searchParams, false);
      }
      window.addEventListener('popstate', onHistoryChange);

      installMediaQueryWatcher('(min-width: 768px)', this._initLayout);
    },
    toggleLayout(theBoolean) {
      this.listLayout = theBoolean;
      localStorage.setItem("list-layout", theBoolean ? "list" : "grid" );
      window.dispatchEvent(new Event('resize'));
    },
    removeFilter(url, clearAll = false) {
      this._captureFilterUiState(document.activeElement);
      this._reloadFilter(url, !clearAll);
    },
    renderPagination() {
      var formData = {
        'attributes': {
          'collection-pagination': this.$el.value             
        }
      }; 
      fetch(Shopify.routes.root+'cart/update', {
        method:'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData)
      }).then(() => {
        this.cachedResults = [];
        const searchParams = this._createSearchParams(document.getElementById('FacetFiltersForm'));
        this._renderPage(searchParams);
      }); 
    },
    onSubmit(wait = 500, sourceEl = null) {
      clearTimeout(this.t);

      const func = () => {
        this._captureFilterUiState(sourceEl || document.activeElement);
        const searchParams = this._createSearchParams(document.getElementById('FacetFiltersForm'));
        this._renderPage(searchParams);
        if(this.isDesktop) document.getElementById('FormSelectFilter').blur();
      }
      
      this.t = setTimeout(() => {
        func();
      }, wait);
    },
    onAvailabilityChange(sourceEl) {
      if (sourceEl.checked) {
        const otherName = sourceEl.name === 'filter.v.availability'
          ? 'store_stock'
          : 'filter.v.availability';
        sourceEl.closest('form')?.querySelectorAll('input').forEach((input) => {
          if (input.name === otherName) input.checked = false;
        });
      }
      this.onSubmit(500, sourceEl);
    },
    clearAvailability(sourceEl) {
      sourceEl.closest('form')?.querySelectorAll('input').forEach((input) => {
        if (input.name === 'filter.v.availability' || input.name === 'store_stock') {
          input.checked = false;
        }
      });
      this.onSubmit(0, sourceEl);
    },
    clearStoreStock(sourceEl) {
      document.getElementById('FacetFiltersForm')?.querySelectorAll('input[name="store_stock"]').forEach((input) => { input.checked = false; });
      this.onSubmit(0, sourceEl);
    },
    rangePrice(el) {
      const rangeInput = el.querySelectorAll(".range-input input"),
      priceInput = el.querySelectorAll(".price-input"),
      pricePreview = el.querySelectorAll(".price-preview"),
      range = el.querySelector(".slider .progress");
      let priceGap = 1;

      rangeInput.forEach((input) => {
        input.addEventListener("input", (e) => {
          e.preventDefault();
          let minVal = Number(rangeInput[0].value).toFixed(2),
            maxVal = Number(rangeInput[1].value).toFixed(2);;
          
          if (maxVal - minVal < priceGap) {
            if (e.target.className === "range-min") {
              rangeInput[0].value = maxVal - priceGap;
              priceInput[0].value = maxVal - priceGap;
            } else {
              rangeInput[1].value = minVal + priceGap;
              priceInput[1].value = maxVal + priceGap;
            }
          } else {
            priceInput[0].value = minVal;
            priceInput[1].value = maxVal;
            range.style.setProperty('--left_range', (minVal / rangeInput[0].max) * 100 + '%');
            range.style.setProperty('--right_range', 100 - (maxVal / rangeInput[1].max) * 100 + '%');
          }
          pricePreview[0].textContent = minVal;
          pricePreview[1].textContent = maxVal;
        });
      });
    },
    initRange(el) {
      const rangeInput = el.querySelectorAll(".range-input input"),
      range = el.querySelector(".slider .progress");
      
      let minVal = Number(rangeInput[0].value).toFixed(2),
      maxVal = Number(rangeInput[1].value).toFixed(2);;
      range.style.setProperty('--left_range', (minVal / rangeInput[0].max) * 100 + '%');
      range.style.setProperty('--right_range',100 - (maxVal / rangeInput[1].max) * 100 + '%');
    },
    _reloadFilter(url, preserveStore = true) {
      const params = new URLSearchParams(url.includes('?') ? url.slice(url.indexOf('?') + 1) : '');
      if (preserveStore && this.storeStockSelected) params.set('store_stock', '1');
      else params.delete('store_stock');
      this._renderPage(params.toString());
    },
    _createSearchParams(form) {
      const formData = new FormData(form);
      return new URLSearchParams(formData).toString();
    },
    _updateURLHash(searchParams) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    },
    _captureFilterUiState(sourceEl) {
      const filter = sourceEl && sourceEl.closest ? sourceEl.closest('.js-filter') : null;
      const values = filter ? filter.querySelector('.ds-facet-values') : null;
      const drawer = sourceEl && sourceEl.closest ? sourceEl.closest('.ds-filter-drawer-scroll') : null;

      this.filterUiState = {
        windowX: window.scrollX,
        windowY: window.scrollY,
        facetIndex: filter ? filter.dataset.index || '' : '',
        facetParam: filter ? filter.dataset.facetParam || '' : '',
        valuesScrollTop: values ? values.scrollTop : 0,
        drawerScrollTop: drawer ? drawer.scrollTop : 0,
        sourceId: sourceEl && sourceEl.id ? sourceEl.id : ''
      };
    },
    _findRememberedFilter(root = document) {
      const state = this.filterUiState;
      if (!state || !state.facetIndex) return null;

      return Array.from(root.querySelectorAll('.js-filter')).find((filter) =>
        filter.dataset.index === state.facetIndex &&
        (!state.facetParam || filter.dataset.facetParam === state.facetParam)
      ) || null;
    },
    _restoreFilterUiState() {
      const state = this.filterUiState;
      if (!state) return;

      const restore = () => {
        const filter = this._findRememberedFilter(document);
        const values = filter ? filter.querySelector('.ds-facet-values') : null;
        if (values) {
          values.scrollTop = Math.min(state.valuesScrollTop, Math.max(0, values.scrollHeight - values.clientHeight));
          this.updateFacetScrollHint(values);
        }

        const drawer = document.querySelector('.form-drawer .ds-filter-drawer-scroll');
        if (drawer && state.drawerScrollTop) drawer.scrollTop = state.drawerScrollTop;

        const source = state.sourceId ? document.getElementById(state.sourceId) : null;
        if (source) {
          try {
            source.focus({ preventScroll: true });
          } catch (error) {
            source.focus();
          }
        }

        window.scrollTo(state.windowX, state.windowY);
      };

      restore();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        restore();
        if (this.filterUiState === state) this.filterUiState = null;
      }));
    },
    async _renderPage(searchParams, updateURLHash = true) {
      const request = ++this.renderRequest;
      this.searchParamsPrev = searchParams;
      this.storeStockSelected = new URLSearchParams(searchParams).get('store_stock') === '1';
      this.loading = true;
      if (this.storeStockSelected) dsStoreStockFacetLoading(document, '…');
      if (document.querySelector('[data-stock-filter-config]')) this._updateStoreStockCount(searchParams);
      document.getElementById('ds-stock-error')?.remove();
      if (updateURLHash) this._updateURLHash(searchParams);
      try {
        let html = this.storeStockSelected ? null : this.cachedResults[searchParams];
        if (!html) {
          {
            const response = await fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams}`);
            if (!response.ok) throw new Error('Collection request failed');
            html = await response.text();
          }
          html = await dsStockFilterPage(html, searchParams, sectionId);
          if (request !== this.renderRequest) return;
          if (!this.storeStockSelected) this.cachedResults[searchParams] = html;
        }
        if (request !== this.renderRequest) return;
        this._renderResults(html);
      } catch (error) {
        if (request !== this.renderRequest) return;
        this.loading = false;
        if (this.storeStockSelected) dsStoreStockFacetLoading(document, '—');
        console.warn('Collection stock filter could not load', error);
        const notice = document.createElement('p');
        notice.id = 'ds-stock-error';
        notice.setAttribute('role', 'alert');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button-outline';
        retry.textContent = document.documentElement.lang === 'cs' ? 'Načtení se nezdařilo. Zkusit znovu' : 'Načítanie sa nepodarilo. Skúsiť znova';
        retry.addEventListener('click', () => this._renderPage(searchParams, false));
        notice.append(retry);
        const grid = document.getElementById('items-grid') || document.getElementById('product-grid');
        if (grid) grid.replaceChildren(notice);
      }
    },
    _renderResults(html) {
      this._renderProductGridContainer(html);
      this._renderProductCount(html);
      this._renderFilters(html);
      this._restoreFilterUiState();
      this.loading = false;
    },
    _renderFilters(html) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      let blockFiltesDrawer = '.form-drawer';
      let blockFiltesAside = '.form-aside';
      const selectBlockFiltesDrawer = document.querySelector(blockFiltesDrawer);
      const selectBlockFiltesAside = document.querySelector(blockFiltesAside);
      let activeTag = document.getElementById("active-filter-tag")

      dsUpdateEmptyFacetGroups(parsedHTML);
      const rememberedFilter = this._findRememberedFilter(parsedHTML);
      if (rememberedFilter && rememberedFilter.dataset.facetEmpty !== 'true') rememberedFilter.setAttribute('x-data', '{open: true}');

      if (activeTag) {
        activeTag.innerHTML = parsedHTML.getElementById("active-filter-tag").innerHTML;   
      }
      if (selectBlockFiltesDrawer) {
        if (this.$el.id) {  
          const eleOpening = parsedHTML.getElementById(this.$el.id) ? parsedHTML.getElementById(this.$el.id).closest('.js-filter') : undefined;
          if (eleOpening && eleOpening.dataset.facetEmpty !== 'true') {
            eleOpening.setAttribute('x-data', '{open: true}');
            parsedHTML.getElementById(this.$el.id).closest('.js-filter').innerHTML = eleOpening.innerHTML;
          }
        }
        selectBlockFiltesDrawer.innerHTML = parsedHTML.querySelector(blockFiltesDrawer).innerHTML;
        this._renderAdditionalElements(parsedHTML);
      };
      if (selectBlockFiltesAside) {
        if (this.$el.id) {
          const eleOpening = parsedHTML.getElementById(this.$el.id) ? parsedHTML.getElementById(this.$el.id).closest('.js-filter') : undefined;
          if (eleOpening && eleOpening.dataset.facetEmpty !== 'true') {
            eleOpening.setAttribute('x-data', '{open: true}');
            parsedHTML.getElementById(this.$el.id).closest('.js-filter').innerHTML = eleOpening.innerHTML;
          }
        }
        selectBlockFiltesAside.innerHTML = parsedHTML.querySelector(blockFiltesAside).innerHTML;
        this._renderAdditionalElements(parsedHTML);
      };
      this._renderAdvancedFilters(parsedHTML);
    },
    _renderAdvancedFilters(html) {
      const destination = document.querySelectorAll(".filter-advanced");
      const source = html.querySelectorAll('.filter-advanced');

      if (source.length > 0 && destination.length > 0) {
        destination.forEach((destination, index) => {
          destination.innerHTML = source[index].innerHTML;
        })
      }
    },
    _renderProductGridContainer(html) {
      const currentContainer = document.getElementById('ProductGridContainer');
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const nextContainer = parsedHTML.getElementById('ProductGridContainer');
      const toolbar = document.getElementById('FacetsWrapperDesktop');

      if (!currentContainer || !nextContainer) return;

      if (toolbar) toolbar.remove();
      currentContainer.innerHTML = nextContainer.innerHTML;

      const toolbarTarget = document.getElementById('header-filter');
      if (toolbar && toolbarTarget) {
        toolbarTarget.appendChild(toolbar);

        const nextToolbarTemplate = parsedHTML.querySelector('template[x-teleport="#header-filter"]');
        const nextToolbar = nextToolbarTemplate
          ? nextToolbarTemplate.content.querySelector('#FacetsWrapperDesktop')
          : null;
        const currentCount = toolbar.querySelector('.ds-toolbar-count');
        const nextCount = nextToolbar ? nextToolbar.querySelector('.ds-toolbar-count') : null;
        const filterButton = toolbar.querySelector('#btn-filter');

        if (currentCount && nextCount) {
          currentCount.dataset.nativeFilterCount = nextCount.dataset.nativeFilterCount;
          const selected = Number(nextCount.dataset.nativeFilterCount) + (this.storeStockSelected ? 1 : 0);
          currentCount.textContent = selected;
          currentCount.style.display = selected ? '' : 'none';
        } else if (currentCount && !nextCount) {
          currentCount.remove();
        } else if (!currentCount && nextCount && filterButton) {
          filterButton.appendChild(nextCount.cloneNode(true));
        }

        const currentSort = toolbar.querySelector('#FormSelectFilter');
        const nextSort = nextToolbar ? nextToolbar.querySelector('#FormSelectFilter') : null;
        if (currentSort && nextSort) currentSort.value = nextSort.value;
      }
    },
    _renderProductCount(html) {
      const productCountEl = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
      if (productCountEl) {
        const count = productCountEl.innerHTML;
        const container = document.getElementById('ProductCount_header');
        const containerDrawer = document.getElementById('ProductCountDrawer');
        const containerDrawerAction = document.getElementById('ProductCountDrawerAction');
        if (container) container.innerHTML = count;
        if (containerDrawer) containerDrawer.innerHTML = count;
        if (containerDrawerAction) {
          const numericCount = count.match(/\d+/);
          containerDrawerAction.textContent = numericCount ? numericCount[0] : count;
        }
        if (container) container.classList.remove('loading');
      }
    },
    _renderAdditionalElements(html) {
      const container = document.getElementById('ProductPerPage');
      if (container) container.innerHTML = html.getElementById('ProductPerPage').innerHTML;
    },
    _filterFocus() {
      Alpine.store('xFocusElement').trapFocus('ProductFilter','CloseFilter');
    },
    _filterRemoveFocus() {
      const activeElement = document.getElementById('btn-filter');
      Alpine.store('xFocusElement').removeTrapFocus(activeElement);
    },
    setFilterHeaderHeight() {
      document.documentElement.style.setProperty('--height-sticky-filter',document.getElementById("FacetsWrapperDesktop").offsetHeight + "px");
    },
    updateFacetScrollHint(el) {
      if (!el) return;
      const shell = el.closest('.ds-facet-values-shell');
      if (!shell) return;

      const isScrollable = el.classList.contains('ds-facet-values--scrollable') &&
        el.scrollHeight > el.clientHeight + 2;
      const isAtBottom = !isScrollable || el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      shell.classList.toggle('is-scrollable', isScrollable);
      shell.classList.toggle('is-at-bottom', isAtBottom);
    },
    setPositionOptionFilter(el) {
      elRect = el.getBoundingClientRect();
      const elPopup = el.getElementsByClassName('popup-above')[0];
      let spacingRight = window.innerWidth - elRect.left;
      let checkSpacing = spacingRight - el.innerWidth;
      if (checkSpacing >= 0) {
        elPopup.style.left = '0px';
      } else {
        elPopup.style.left = checkSpacing+ 'px';
      }
    },
    getPopupPosition(el) {
      this.$nextTick(() => {
        const elPopup = el.getElementsByClassName('popup-above')[0];
        if (elPopup) {
          const popupRect = elPopup.getBoundingClientRect();
          if( popupRect.right > window.innerWidth){
            elPopup.style.right = 0;
          }
        }
      });
    }
  }));
});
